import { Banner, Button, DataTable, Section, notify, type Column } from "@flexfreight/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { api, ApiError } from "../../api/client";
import { CustomerLink, message, money, when } from "./ops";

/* Freight > Billing reconciliation: invoices whose Zoho Books sync failed for good (create, email or PDF), with a
 * Retry that finishes the missing step. Admin Center > Problem reports shows the count with a link here. */

export type ZohoFailure = {
  id: string;
  orgId: string;
  orgName: string;
  shipmentId: string;
  number: string;
  bolNumber: string | null;
  issuedAt: string;
  amount: { cents: number; currency: "USD" };
  stage: "create" | "after_create";
  zohoInvoiceNumber: string | null;
  lastError: string;
  attempts: number;
  failedAt: string;
};
export type ZohoFailuresView = { failures: ZohoFailure[]; mode: "mock" | "live"; source: "console" | "env" | "mock" };
export const ZOHO_FAILURES_KEY = ["console", "billing", "zoho-failures"];

export function useZohoFailures(enabled = true) {
  return useQuery({ queryKey: ZOHO_FAILURES_KEY, queryFn: () => api<ZohoFailuresView>("/v1/console/billing/zoho-failures"), enabled, refetchInterval: 60_000 });
}

export function ZohoFailuresSection({ canRetry }: { canRetry: boolean }) {
  const qc = useQueryClient();
  const q = useZohoFailures();
  const retry = useMutation({
    mutationFn: (id: string) => api<{ ok: boolean; zohoInvoiceNumber?: string; emailed?: boolean; pdfStored?: boolean; error?: string; failures: ZohoFailure[] }>(`/v1/console/billing/zoho-failures/${id}/retry`, { method: "POST" }),
    onSuccess: (r) => {
      qc.setQueryData(ZOHO_FAILURES_KEY, (cur: ZohoFailuresView | undefined) => (cur ? { ...cur, failures: r.failures } : cur));
      void qc.invalidateQueries({ queryKey: ["console", "customer"] });
      if (r.ok) notify.success("Synced to Zoho Books", [r.zohoInvoiceNumber ? `Zoho invoice ${r.zohoInvoiceNumber}.` : null, r.emailed ? "Emailed to the customer." : null, r.pdfStored ? "PDF stored." : null].filter(Boolean).join(" ") || undefined);
      else notify.error("Still failing", r.error);
    },
    onError: (e) => notify.error("Couldn't retry", e instanceof ApiError ? e.message : message(e)),
  });
  const rows = q.data?.failures ?? [];
  if (!q.isPending && rows.length === 0) return null;
  const columns: Column<ZohoFailure>[] = [
    { key: "invoice", header: "Invoice", primary: true, width: "9rem", render: (f) => <span className="ffx-num">{f.number}</span>, sub: (f) => f.bolNumber ?? null },
    { key: "customer", header: "Customer", width: "14rem", render: (f) => <CustomerLink orgId={f.orgId} name={f.orgName} /> },
    { key: "amount", header: "Amount", numeric: true, width: "7rem", render: (f) => money(f.amount.cents) },
    { key: "stage", header: "Failed at", width: "9rem", render: (f) => (f.stage === "create" ? "Creating in Zoho" : "Email / PDF"), sub: (f) => `${f.attempts} attempt${f.attempts === 1 ? "" : "s"} · ${when(f.failedAt)}` },
    { key: "error", header: "Reason", truncate: true, text: (f) => f.lastError, render: (f) => f.lastError },
    ...(canRetry ? [{ key: "retry", header: "", width: "6rem", render: (f: ZohoFailure) => <Button size="sm" loading={retry.isPending && retry.variables === f.id} onClick={() => retry.mutate(f.id)}>Retry</Button> } as Column<ZohoFailure>] : []),
  ];
  return (
    <Section title="Zoho Books" description="Invoices that could not be created, emailed or saved as a PDF in Zoho Books after every automatic retry. Retry finishes the missing step; nothing is duplicated." flush>
      <DataTable caption="Zoho Books sync failures" columns={columns} rows={rows} rowKey={(f) => f.id} loading={q.isPending} error={q.isError ? { title: "Couldn't load Zoho failures", message: message(q.error), onRetry: () => void q.refetch() } : null} empty="Nothing failed." rowTone={() => "danger"} maxHeight="none" />
    </Section>
  );
}

/** Admin Center > Problem reports: one line when Zoho Books invoices need attention. */
export function ZohoFailuresNotice() {
  const q = useZohoFailures();
  const n = q.data?.failures.length ?? 0;
  if (!n) return null;
  return (
    <Banner tone="warning" actions={<Link className="ffx-btn ffx-btn--sm" to="/billing">Open Billing</Link>}>
      {n} invoice{n === 1 ? "" : "s"} could not be synced to Zoho Books. Retry them from Billing reconciliation.
    </Banner>
  );
}
